import { useEffect, useMemo, useRef, useState } from 'react';
import { MdOutlineAddToPhotos, MdOutlineBrokenImage } from 'react-icons/md';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useSearchParams } from 'react-router-dom';
import { IoList } from 'react-icons/io5';
import { Button, ButtonTheme, ButtonVariant, Heading, InfoPanel, SpinnerLite, TypographyColor, TypographyType } from '../../ui-elements';
import * as Constants from '../../utils/constants/Constants';
import { BreakDownTableComponent, LineChartComponentcatSubCat } from '.';
import { CustomDropdown } from '../../ui-elements/customDropdown/CustomDropdown';
import { geoState, selectedCategoryState, selecteddropDownDataState, selectedGeoDropdownState, showTableState, spinnerLiteState, spinnerState } from '../../states';
import { useHandleError } from '../../helpers';
import { useDashboardService } from '../../services';

interface GrowthInTimeChartProps {
    headerText: string,
    infoText: string,
    isIconVisisble: boolean
    isDropdownVisible: boolean
    data: any,
    color: string,
    breakDownType: string,
    setSelectedYear: (selectedYear: string) => void,
    selectedYear: string,
    setIsDropdownVisible: (isDropdownVisible: boolean) => void,
}

export const GrowthInTimeLineChartSubCat = ({ headerText, infoText, isIconVisisble, isDropdownVisible, setIsDropdownVisible, data, color, breakDownType, selectedYear, setSelectedYear }: GrowthInTimeChartProps) => {
    const spinner = useRecoilValue(spinnerState);
    const [spinnerLite, setSpinnerLite] = useRecoilState(spinnerLiteState);
    const { getErrorMsg } = useHandleError();
    const dashboardService = useDashboardService();
    const selectedGeo = useRecoilValue(geoState);
    const [searchParams] = useSearchParams();

    // Preserve dropdown selection across year changes
    const [selectedDropDownData, setSelectedDropDownData] = useRecoilState(selecteddropDownDataState);
    const [selectedDataIndex, setSelectedDataIndex] = useState(0);
    const [isSingleLine, setIsSingleLine] = useState(true);
    const [showTable, setshowTable] = useRecoilState(showTableState);
    const [lineChartData, setLineChartData] = useState<any>([]);
    const selectedCategory = useRecoilValue(selectedCategoryState);
    const [growthRateAllSubCategories, setGrowthRateAllSubCategories] = useState({ type: '', tableHeaderData: [], tableData: [] });
    const prevCategory = useRef(selectedCategory);
    const [dropDownData, setDropDownData] = useState(data);
    const [selTableHeader, setSelTableHeader] = useState<any>({ eh: 'EH', ctv: 'CTV', PoI: "PoI" });
    const selectedGeoDropdown = useRecoilValue(selectedGeoDropdownState);

    const geoCode = useMemo(() => {
        const { country, state, district, sub_district, village } = selectedGeo;
        const geo_id = village || sub_district || district || state || country;
        return Number(geo_id);
    }, [selectedGeo]);

    useEffect(() => {
        setLineChartData(data);
        setDropDownData(data);
        // Reset to 0th index if current selection doesn't exist in new data
        if (data?.length > 0) {
            const exists = data.some((item: any) => item.name === selectedDropDownData);
            if (!exists) {
                setSelectedDropDownData(data[0]?.name || '');
            }
        }
    }, [data]);

    useEffect(() => {
        if (prevCategory.current !== selectedCategory) {
            // Category changed, reset to first item
            setDropDownData(data);
            setSelectedDropDownData(data?.[0]?.name || '');
            prevCategory.current = selectedCategory;
        }
    }, [selectedCategory, data]);

    useEffect(() => {
        if (Object.keys(selectedDropDownData).length === 0) {
            setDropDownData(data);
            setSelectedDropDownData(data?.[0]?.name || '');
        }
    }, []);

    const handleSingleLine = () => {
        setshowTable(false);
        setIsSingleLine(true);
    };

    const fetchGrowthRateAllSubCategories = async (payload: any) => {
        setGrowthRateAllSubCategories({ type: '', tableHeaderData: [], tableData: [] });
        setSpinnerLite(prev => prev + 1);
        try {
            const response = await dashboardService.getGrowthRateAllSubCategories(payload);
            if (response) {
                setGrowthRateAllSubCategories(response);
                setSpinnerLite(prev => prev - 1);
            }
        } catch (error) {
            getErrorMsg(error);
            setSpinnerLite(prev => prev - 1);
        }
    }

    const handleAllLine = () => {
        setshowTable(false);
        setIsSingleLine(false);
    };

    const handleShowTable = () => {
        setshowTable(true);
        setIsDropdownVisible(false);
        if (geoCode && selectedCategory.key && selectedCategory.name && selectedYear) {
            // Check if catchment is present in searchParams
            const catchmentId = searchParams.get('catchment');
            const effectiveGeoId = catchmentId ? Number(catchmentId) : geoCode;
            var payload =
            {
                "geoId": effectiveGeoId,
                "categoryId": selectedCategory.key,
                "category": selectedCategory.name,
                "timePeriod": Number(selectedYear),
                ...(selectedGeoDropdown?.geotype === "pincode" && { parentgeoId: Number(selectedGeo.district) }),
                ...(selectedGeoDropdown?.geotype === "pincode" && { type: "pincode" })
            }
            fetchGrowthRateAllSubCategories(payload);
        }
    }

    useEffect(() => {
        if (showTable) {
            handleShowTable();
        } else if (isSingleLine && headerText !== "Sub District" && headerText !== "Pin Code") {
            // Filter data for single line
            const filteredData = data.filter(
                (category: any) => category.name === "Average" || category.name === selectedDropDownData
            );
            setLineChartData(filteredData);
        } else {
            // Reset to all lines
            setLineChartData(data);
        }
    }, [isSingleLine, selectedDropDownData, selectedYear]);

    const handleChangeData = (value: any) => {
        setSelectedDropDownData(value);
    };

    useEffect(() => {
        if (selectedDropDownData) {
            const index = lineChartData.findIndex(
                // (item: any) => item.name.toString().trim() === selectedDropDownData.toString().trim()
                (item: any) => item.name.toString().trim() === String(selectedDropDownData || "").trim()
            );
            setSelectedDataIndex(index !== -1 ? index : 0); // Default to 0 if not found
        }
    }, [selectedDropDownData, lineChartData]);

    const handleyearChange = (year: string) => {
        setSelectedYear(year); // Update the selected year when clicked
        if (showTable) {
            handleShowTable();
        }
    };

    useEffect(() => { console.log("data:",data) }, [data]);

    return (
        <div style={{ height: '', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <div
                className='w-100 d-flex flex-row justify-content-between align-items-end margin-bottom-4'
            >
                <div className="w-50 d-flex flex-row justify-content-start align-items-center">
                    <Heading
                        title={`Growth of Pols over time for ${headerText}`}
                        type={TypographyType.h5}
                        colour={TypographyColor.secondary}
                        classname='text-start'
                    />
                    <InfoPanel text={infoText} classname='margin-left-2 fs-14' />
                </div>
                <div
                    className="w-50 margin-top-3 height-4vh w-25 d-flex flex-row justify-content-end" style={{ gap: '1.320vw' }}
                >
                    <div
                        style={{ borderRadius: '0.703125vh', }}
                        className="linerchart-border-color margin-bottom-2 w-25 height-4vh d-flex flex-row justify-content-between align-items-center"
                    >
                        {Constants.yearHeader.map((item) => (
                            <Heading
                                title={`${item}${item === "1" ? "Yr" : "Yrs"}`}
                                type={TypographyType.h5}
                                colour={TypographyColor.primary}
                                classname={`margin-left-2 margin-right-2 height-4vh d-flex align-items-center ${selectedYear === item && "border-b-black-sm"}`}
                                onClick={() => handleyearChange(item)}
                                key={item}
                            />
                        ))}
                    </div>
                    {!showTable && isDropdownVisible &&
                        // <Select
                        //     options={data ? data.filter(
                        //         (item: any) => item.name !== "Average"
                        //     ) : "No data"}
                        //     onChange={(e) => handleChangeData(e)}
                        //     value={selectedDropDownData}
                        //     labelKey="name"
                        //     valueKey="name"
                        //     size={SelectSize.medium}
                        //     name="country"
                        //     classname="linechartDropdownBorder height-4vh"
                        // />
                        <CustomDropdown
                            options={dropDownData || []} // Ensure data is not undefined
                            selectedValue={selectedDropDownData}
                            onSelect={handleChangeData}
                            labelKey="name" // Ensure `name` exists in data
                            valueKey="name"
                            classname="custom-dropdown height-4vh-custom"
                            style={{ minWidth: '20vh', maxWidth: '100%', height: '4vh' }}

                        />
                    }
                    {isIconVisisble &&
                        <div className='d-flex flex-row align-items-center' style={{ gap: '1.320vw' }}>
                            {/* <Button
                                theme={ButtonTheme.secondary}
                                variant={ButtonVariant.bordered}
                                type='button'
                                classname='linechart-icon-wrapper'
                            >
                                <IoIosSearch className='fs-16 header-icon' onClick={() => handleSearchClick()} />
                            </Button> */}
                            <Button
                                theme={ButtonTheme.secondary}
                                variant={ButtonVariant.bordered}
                                type='button'
                                onClick={handleSingleLine}
                                classname={`linechart-icon-wrapper  ${isSingleLine && !showTable ? "backgroundColor" : ''}`}

                            >
                                <MdOutlineBrokenImage className={`fs-16 header-icon cursor-pointer ${isSingleLine && !showTable ? "color-black" : ''}`} />
                            </Button>

                            <Button
                                theme={ButtonTheme.secondary}
                                variant={ButtonVariant.bordered}
                                type='button'
                                onClick={handleAllLine}
                                classname={`linechart-icon-wrapper  ${!isSingleLine && !showTable ? "backgroundColor" : ''}`}
                            >
                                <MdOutlineAddToPhotos className={`fs-16 header-icon cursor-pointer ${!isSingleLine && !showTable ? "color-black" : ''}`} />
                            </Button>
                            <Button
                                theme={ButtonTheme.secondary}
                                variant={ButtonVariant.bordered}
                                type='button'
                                onClick={handleShowTable}
                                classname={`linechart-icon-wrapper  ${showTable ? "backgroundColor" : ''}`}
                            >
                                <IoList className={`fs-16 header-icon cursor-pointer ${showTable ? "color-black" : ''}`} />
                            </Button>
                        </div>
                    }
                </div>
            </div>

            {showTable ?
                 Object.keys(growthRateAllSubCategories).length === 0
                    ? <div className='w-100 '>
                        <SpinnerLite />
                    </div>
                    :  growthRateAllSubCategories?.tableData.length > 0 &&
                    <div className="w-100">
                        <BreakDownTableComponent tableContent={growthRateAllSubCategories} searchTerm={''} setSelTableHeader={setSelTableHeader}
                            selTableHeader={selTableHeader} type={'poigraph'}/>
                    </div>
                : <div style={{ borderRadius: '0.814vh', borderColor: '#F4F4F4' }} className='w-100 border d-flex flex-column align-items-center justify-content-center'
                >
                    <div className="p-4 w-100">
                        <LineChartComponentcatSubCat
                            graphData={lineChartData}
                            selectedData={selectedDropDownData}
                            selectedDataIndex={selectedDataIndex}
                            setSelectedDataIndex={setSelectedDataIndex}
                            setSelectedData={setSelectedDropDownData}
                            color={color}
                            headerText={headerText}
                            breakDownType={breakDownType}
                            selectedYear={selectedYear}
                            isSingleLine={isSingleLine}
                        />
                    </div>
                    <div className="w-100 p-4 border-top d-flex flex-row justify-content-center align-items-center gap-3">
                        {lineChartData?.[selectedDataIndex]?.cagrText?.map((data: any) =>
                        (<div className='d-flex flex-row justify-content-center align-items-center gap-1'>
                            <span className='poi-circle bg-black-1' style={{ backgroundColor: `${data?.color}` }}></span>
                            <Heading
                                title={`${data?.label}`}
                                type={TypographyType.h5}
                                colour={TypographyColor.primary}
                            />
                        </div>))
                        }
                    </div>
                </div>
            }
            {/* </div> */}
        </div>
    );
};

